'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { measureDescriptionPreview } from './measure-description-preview';
import styles from './product-short-description.module.css';

export function ProductShortDescription({ html }: { html: string }) {
  const id = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const limitRef = useRef<HTMLSpanElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [previewHeight, setPreviewHeight] = useState<number>();

  useEffect(() => {
    const content = contentRef.current;
    const limit = limitRef.current;
    if (!content || !limit) return;

    const measure = () => {
      const height = measureDescriptionPreview(content, limit.getBoundingClientRect().height);
      const next = content.getBoundingClientRect().height > height + 1;
      setPreviewHeight(height);
      setOverflowing(next);
      if (!next) setExpanded(false);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    observer.observe(limit);
    return () => observer.disconnect();
  }, [html]);

  return (
    <div className={cn(styles.description, 'mt-2 ea-product-detail-header__product-description')}>
      <span ref={limitRef} className={styles.limit} aria-hidden="true" />
      <div
        id={id}
        ref={previewRef}
        className={cn(styles.preview, 'ea-product-short-description-preview')}
        data-expanded={expanded}
        data-ea-overflow-fade="off"
        style={
          {
            '--measured-preview-height': previewHeight ? `${previewHeight}px` : undefined,
          } as CSSProperties
        }
        onFocusCapture={() => {
          // Reveal links and other focusable content before keyboard users reach the clipped area.
          if (overflowing) setExpanded(true);
        }}
      >
        <div ref={contentRef} className={styles.content}>
          <HtmlRenderer
            html={html}
            className="ea-product-detail-header__product-description-content"
          />
        </div>
      </div>
      {overflowing && (
        <Button
          type="button"
          variant="link"
          aria-expanded={expanded}
          aria-controls={id}
          className={cn(
            styles.toggle,
            'mt-2 justify-start gap-1 p-0 underline hover:text-muted-foreground',
            'ea-product-detail-header__product-description-button',
          )}
          onClick={() => {
            setExpanded(!expanded);
            if (expanded) {
              requestAnimationFrame(() => {
                const preview = previewRef.current;
                if (preview && preview.getBoundingClientRect().top < 0) {
                  preview.scrollIntoView({ block: 'start' });
                }
              });
            }
          }}
        >
          {expanded ? 'Zobraziť menej' : 'Zobraziť viac'}
          {expanded ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
        </Button>
      )}
      <noscript>
        <style>{'.ea-product-short-description-preview{max-height:none!important}'}</style>
      </noscript>
    </div>
  );
}
